import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Assignment, Tune, FilterList } from '@mui/icons-material';
import { shopTaskService } from '../services/shopTaskService';
import { formatDateOnly } from '../utils/dateUtils';
import usePersistedListFilters from '../hooks/usePersistedListFilters';

export function taskStatusChip(task) {
  if (task.is_default) return <Chip size="small" label="Default" color="primary" variant="outlined" />;
  const orderClosed = ['shipped', 'cancelled'].includes(String(task.work_order_status || '').toLowerCase());
  if (task.status === 'closed') return <Chip size="small" label="Closed" />;
  if (orderClosed) return <Chip size="small" label="Order closed" color="warning" variant="outlined" />;
  return <Chip size="small" label="Open" color="success" />;
}

const LIST_FILTERS_DEFAULTS = {
  search: '',
  status: 'open',
  page: 0
};

const ShopTasks = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    values: listFilters,
    setField: setListFilter,
    setValues: setListFilters,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_shop_tasks_list_filters', LIST_FILTERS_DEFAULTS);
  const search = listFilters.search;
  const setSearch = useCallback((v) => setListFilter('search', v), [setListFilter]);
  const status = listFilters.status;
  const setStatus = useCallback((v) => setListFilter('status', v), [setListFilter]);
  const page = listFilters.page;
  const setPage = useCallback((v) => setListFilter('page', v), [setListFilter]);
  const handleClearFilters = useCallback(() => {
    clearListFilters();
    setSearchParams({}, { replace: true });
  }, [clearListFilters, setSearchParams]);

  const seededFromUrlRef = useRef(false);
  useEffect(() => {
    if (seededFromUrlRef.current) return;
    seededFromUrlRef.current = true;
    const hasUrlFilters =
      searchParams.has('q') || searchParams.has('status') || searchParams.has('page');
    if (!hasUrlFilters) return;
    setListFilters({
      search: searchParams.get('q') || '',
      status: searchParams.get('status') ?? 'open',
      page: Number(searchParams.get('page') || 0) || 0
    });
  }, [searchParams, setListFilters]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await shopTaskService.getAll({
        search: search || undefined,
        status: status || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      setRows(res.data || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [search, status, page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const next = {};
    if (search) next.q = search;
    if (status !== 'open') next.status = status;
    if (page) next.page = String(page);
    setSearchParams(next, { replace: true });
  }, [search, status, page, setSearchParams]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Assignment color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>
          Shop Tasks
        </Typography>
        <Button variant="outlined" startIcon={<Tune />} onClick={() => navigate('/shop-tasks/allocation')}>
          Labor Allocation
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/shop-tasks/new')}>
          New Task
        </Button>
      </Box>

      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search"
          placeholder="Order, function, customer, notes"
          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => {
              setPage(0);
              setStatus(e.target.value);
            }}
          >
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
            <MenuItem value="">All</MenuItem>
          </Select>
        </FormControl>
        {hasActiveFilters ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterList />}
            onClick={handleClearFilters}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Clear filters
          </Button>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
          {total === 0 ? 'No tasks match the current filters.' : `${total} task${total === 1 ? '' : 's'}`}
        </Typography>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Task</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Functions</TableCell>
              <TableCell>Shop Notes</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell align="right">People</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No tasks yet. Create one so employees can clock into it at the kiosk.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/shop-tasks/${row.id}`)}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                  <TableCell>{row.work_order_identifier || row.work_order_number || '—'}</TableCell>
                  <TableCell>{row.customer_name || '—'}</TableCell>
                  <TableCell>{row.function_labels || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 260 }} title={row.shop_notes || ''}>
                      {row.shop_notes || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{Number(row.hours_logged || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">{row.employee_count || 0}</TableCell>
                  <TableCell>{taskStatusChip(row)}</TableCell>
                  <TableCell>{formatDateOnly(row.created_at) || '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

export default ShopTasks;
